import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { formatNumber } from "../utils/format.ts";

interface BulkActionBarProps {
  /** How many rows are selected: "N selected". */
  count: number;
  /** Replaces "N selected" ("All 1,204 matching selected"). */
  label?: ReactNode;
  /** The actions on the selection: small buttons, the primary one contained. */
  children: ReactNode;
  onClear: () => void;
  /**
   * Floats at the bottom of the viewport while the table scrolls under it (the
   * default, under the table). Off for a bar that sits above its table.
   */
  sticky?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * What to do with the rows ticked in a table: the count, the actions and
 * "Clear". Render it only while something is selected.
 */
export default function BulkActionBar({
  count,
  label,
  children,
  onClear,
  sticky = true,
  sx,
}: BulkActionBarProps) {
  return (
    <Paper
      variant="outlined"
      sx={[
        {
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          rowGap: 1,
          flexWrap: "wrap",
          px: 2,
          py: 1,
        },
        // It floats over the rows, so it carries the one shadow page content may.
        sticky && { position: "sticky", bottom: 16, zIndex: 2, boxShadow: 3 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
        {label ?? `${formatNumber(count)} selected`}
      </Typography>
      {children}
      <Button size="small" onClick={onClear} sx={{ ml: "auto" }}>
        Clear
      </Button>
    </Paper>
  );
}
